"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { getFirstAvailableDateAndTime } from "@/lib/availabilityUtils";

export interface CartItem {
  id: string;
  serviceId: string;
  subServiceId: string | null;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  startDate?: Date;
  endDate?: Date;
  startTime?: string;
  endTime?: string;
  reservation?: any;
  isQuantityFixed?: boolean;
  data: string | null;
  serviceAvailability?: number;
  maxDailyQuantity?: number | null;
  bookedDailyQuantities?: Record<string, number>;
  isCustomSchedule?: boolean;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  updateItemDates: (id: string, startDate?: Date, endDate?: Date) => void;
  updateItemTimes: (id: string, startTime?: string, endTime?: string) => void;
  resetItemScheduleToGlobal: (id: string) => void;
  globalStartDate?: Date;
  globalEndDate?: Date;
  globalStartTime?: string;
  globalEndTime?: string;
  setGlobalSchedule: (schedule: {
    startDate?: Date;
    endDate?: Date;
    startTime?: string;
    endTime?: string;
  }) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);

  const [globalStartDate, setGlobalStartDate] = useState<Date | undefined>(
    undefined,
  );
  const [globalEndDate, setGlobalEndDate] = useState<Date | undefined>(
    undefined,
  );
  const [globalStartTime, setGlobalStartTime] = useState<string | undefined>(
    "10:00",
  );
  const [globalEndTime, setGlobalEndTime] = useState<string | undefined>(
    "11:00",
  );

  const setGlobalSchedule = useCallback(
    (schedule: {
      startDate?: Date;
      endDate?: Date;
      startTime?: string;
      endTime?: string;
    }) => {
      setGlobalStartDate(schedule.startDate);
      setGlobalEndDate(schedule.endDate);
      setGlobalStartTime(schedule.startTime);
      setGlobalEndTime(schedule.endTime);

      setItems((prev) =>
        prev.map((item) => {
          if (item.isCustomSchedule) {
            return item;
          }
          return {
            ...item,
            startDate: schedule.startDate ?? item.startDate,
            endDate: schedule.endDate ?? item.endDate,
            startTime: schedule.startTime ?? item.startTime,
            endTime: schedule.endTime ?? item.endTime,
          };
        }),
      );
    },
    [],
  );

  const addItem = useCallback(
    (item: Omit<CartItem, "quantity">) => {
      setItems((prev) => {
        const existingItem = prev.find((i) => i.id === item.id);

        let minDate = new Date();
        if (item.reservation?.arrival) {
          const arr = new Date(item.reservation.arrival);
          if (!isNaN(arr.getTime())) {
            minDate = arr;
          }
        }

        const firstAvail = getFirstAvailableDateAndTime(
          minDate,
          item.serviceAvailability ?? 0,
          item.data ?? undefined,
        );

        let itemStartDate =
          item.startDate ?? globalStartDate ?? firstAvail.startDate;
        let itemEndDate = item.endDate ?? globalEndDate ?? firstAvail.endDate;
        let itemStartTime =
          item.startTime ?? globalStartTime ?? firstAvail.startTime;
        let itemEndTime = item.endTime ?? globalEndTime ?? firstAvail.endTime;

        if (!globalStartDate) {
          setGlobalStartDate(itemStartDate);
        }
        if (!globalEndDate) {
          setGlobalEndDate(itemEndDate);
        }
        if (!globalStartTime) {
          setGlobalStartTime(itemStartTime);
        }
        if (!globalEndTime) {
          setGlobalEndTime(itemEndTime);
        }

        if (existingItem && !item.isQuantityFixed) {
          return prev.map((i) =>
            i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i,
          );
        } else if (existingItem && item.isQuantityFixed === true) {
          return prev;
        } else {
          return [
            ...prev,
            {
              ...item,
              quantity: 1,
              startDate: itemStartDate,
              endDate: itemEndDate,
              startTime: itemStartTime,
              endTime: itemEndTime,
              isCustomSchedule: item.isCustomSchedule ?? false,
            },
          ];
        }
      });
    },
    [globalStartDate, globalEndDate, globalStartTime, globalEndTime],
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((item) => item.id !== id));
    } else {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, quantity } : item)),
      );
    }
  }, []);

  const updateItemDates = useCallback(
    (id: string, startDate?: Date, endDate?: Date) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, startDate, endDate, isCustomSchedule: true }
            : item,
        ),
      );
    },
    [],
  );

  const updateItemTimes = useCallback(
    (id: string, startTime?: string, endTime?: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, startTime, endTime, isCustomSchedule: true }
            : item,
        ),
      );
    },
    [],
  );

  const resetItemScheduleToGlobal = useCallback(
    (id: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                startDate:
                  globalStartDate ??
                  (item.reservation?.arrival
                    ? new Date(item.reservation.arrival)
                    : undefined),
                endDate: globalEndDate,
                startTime: globalStartTime ?? "10:00",
                endTime: globalEndTime ?? "11:00",
                isCustomSchedule: false,
              }
            : item,
        ),
      );
    },
    [globalStartDate, globalEndDate, globalStartTime, globalEndTime],
  );

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        updateItemDates,
        updateItemTimes,
        resetItemScheduleToGlobal,
        globalStartDate,
        globalEndDate,
        globalStartTime,
        globalEndTime,
        setGlobalSchedule,
        clearCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
